import app from 'flarum/forum/app';
import Component, { ComponentAttrs } from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';
import Button from 'flarum/common/components/Button';
import Group from 'flarum/common/models/Group';
import Discussion from 'flarum/common/models/Discussion';
import Link from 'flarum/common/components/Link';
import type Subscription from '../../common/Subscription';
import type Mithril from 'mithril';
import { ApiPayloadSingle } from 'flarum/common/Store';

export interface ISubscriptionCardAttrs extends ComponentAttrs {
  subscription: Subscription;
}

export default class SubscriptionCard<CustomAttrs extends ISubscriptionCardAttrs = ISubscriptionCardAttrs> extends Component<CustomAttrs> {
  private subscription!: Subscription | null;
  private actor = app?.session?.user;
  private loading = false;

  oninit(vnode: Mithril.Vnode<CustomAttrs, this>) {
    super.oninit(vnode);

    this.subscription = this.attrs.subscription;
  }

  view() {
    if (this.subscription === null) {
      return this.empty();
    }

    const related = this.subscription.relatesTo<Discussion | Group>();

    if (related instanceof Group) {
      return this.group(related);
    }

    if (related instanceof Discussion) {
      return this.discussion(related);
    }

    return null;
  }

  empty() {
    return (
      <div className="SubscribeCard">
        <span className="icon">{icon('fas fa-ban')}</span>
        <h2 className="group">{app.translator.trans('floxum-payments.forum.subscription-card.none')}</h2>
        <div className="description"></div>
        <div className="controls"></div>
      </div>
    );
  }

  discussion(discussion: Discussion) {
    return (
      <div className="SubscribeCard">
        <span className="icon"></span>
        <h2 className="group">{app.translator.trans('floxum-payments.forum.subscription-card.' + this.subscription!.paymentType())}</h2>
        <Link href={app.route.discussion(discussion)} className="description">
          "{discussion.title()}" ({this.subscriptionInfo()})
        </Link>
        <div className="controls">{this.button()}</div>
      </div>
    );
  }

  group(group: Group) {
    return (
      <div className="SubscribeCard" style={group.color() ? { backgroundColor: group.color() } : ''}>
        <span className="icon">{icon(group.icon() || '')}</span>
        <h2 className="group">{app.translator.trans('floxum-payments.forum.subscription-card.' + this.subscription!.paymentType())}</h2>
        <div className="description">
          {group.namePlural()} ({this.subscriptionInfo()})
        </div>
        <div className="controls">{this.button()}</div>
      </div>
    );
  }

  subscriptionInfo() {
    return this.subscription!.amount() + ' ' + this.subscription!.interval();
  }

  button() {
    if (!this.subscription!.cancellable()) return [];

    return Button.component(
      {
        icon: 'fas fa-hourglass-end',
        className: 'Button',
        onclick: () => this.cancelSubscription(),
        disabled: this.loading,
      },
      app.translator.trans('floxum-payments.forum.subscription-card.cancel-control')
    );
  }

  cancelSubscription() {
    this.loading = true;

    app
      .request<ApiPayloadSingle>({
        url: app.forum.attribute<string>('apiUrl') + '/blomstra-payments/stripe/user-subscriptions/' + this.subscription!.id(),
        method: 'delete',
      })
      .then((response) => {
        this.subscription = app.store.pushPayload<Subscription>(response);

        this.loading = false;

        m.redraw();
      });
  }
}
